import { Card } from "./ui.js";
import { isIos, isStandalone, useInstallPrompt } from "../lib/pwa.js";

/**
 * "Install" in the header — only when the browser has actually offered it.
 *
 * Nothing renders otherwise: an install button that does nothing on iOS, or on
 * a phone where the app is already installed, is worse than no button.
 */
export function InstallButton({ className = "" }: { className?: string }) {
  const { canInstall, install } = useInstallPrompt();
  if (!canInstall) return null;

  return (
    <button
      type="button"
      className={`btn-ghost px-3 py-1.5 ${className}`}
      onClick={() => void install()}
      title="Add the Madden Pro League to your home screen"
    >
      Install app
    </button>
  );
}

/**
 * The same thing explained, for the platform that won't offer it.
 *
 * Safari on iOS installs only through the Share sheet and fires no event, so
 * there is nothing to hook — the instructions are the feature.
 */
export function InstallCard() {
  const { canInstall, installed, install } = useInstallPrompt();

  if (installed || isStandalone()) {
    return (
      <Card title="The app" className="mt-6">
        <p className="px-5 py-4 text-sm text-chalk-400">
          You're running the installed app. It updates itself whenever the site
          does — there's nothing to keep up to date.
        </p>
      </Card>
    );
  }

  return (
    <Card title="Install the app" className="mt-6">
      <div className="space-y-3 px-5 py-4 text-sm text-chalk-400">
        <p>
          The site installs to your phone or desktop like an app: its own icon,
          full screen, no browser bar, and it opens straight to the league.
        </p>

        {canInstall ? (
          <button type="button" className="btn-primary" onClick={() => void install()}>
            Install
          </button>
        ) : isIos() ? (
          <p>
            On iPhone or iPad, tap <span className="text-chalk-200">Share</span>{" "}
            at the bottom of Safari, then{" "}
            <span className="text-chalk-200">Add to Home Screen</span>. It has to
            be Safari — other iOS browsers can't install it.
          </p>
        ) : (
          <p>
            In Chrome or Edge, open the browser menu and choose{" "}
            <span className="text-chalk-200">Install</span> (sometimes{" "}
            <span className="text-chalk-200">Add to Home screen</span>). If it
            isn't there, the browser you're using doesn't support it.
          </p>
        )}
      </div>
    </Card>
  );
}
